import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Inbox, FolderX } from "lucide-react";
import { EmptyState, Skeleton } from "@dfws/ui";
import { updateSettings, useSettings } from "../../settings/store";
import { useShortcuts } from "../../command/shortcuts";
import { useMediaQuery } from "../../app/useMediaQuery";
import { findFolder, folderLabel } from "../../app/nav";
import { prefetchConversation, useMailboxes, useMessageAction, useThreads } from "../../lib/queries";
import { toListThread, type ListThread } from "./types";
import { MessageList } from "./MessageList";
import { ThreadView } from "./ThreadView";
import { SplitPane } from "./SplitPane";
import { MailToolbar } from "./MailToolbar";
import s from "./MailView.module.css";

/** Keyed by folder in the router, so cursor, open thread and selection reset when the folder changes. */
export function MailView({ folder }: { folder: string }) {
  const settings = useSettings();
  const isPhone = useMediaQuery("(max-width: 899px)");
  const navigate = useNavigate();
  const mailboxes = useMailboxes();
  const box = findFolder(mailboxes.data?.items, folder);
  const pages = useThreads(box?.id);
  const action = useMessageAction();

  const threads: ListThread[] = useMemo(() => pages.data?.pages.flatMap((p) => p.items.map(toListThread)) ?? [], [pages.data]);
  const total = pages.data?.pages[0]?.total ?? 0;

  const [cursor, setCursor] = useState(0);
  // The cursor ring only shows once the keyboard is used, like Gmail; a click hides it again.
  const [keyboardNav, setKeyboardNav] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const anchor = useRef<number>(0);

  const split = isPhone ? "none" : settings.splitView;
  const listOnly = split === "none";
  const showingThread = listOnly && openId !== null;
  const listWidth = useListWidth(split === "right" ? (settings.paneSizes["mail-right"] ?? 42) : 100);
  const twoLine = isPhone || listWidth < 640;

  const showImages = settings.externalImages === "always";
  const warm = useCallback(
    (index: number) => {
      const t = threads[index];
      return t ? prefetchConversation(t.id, t.latestMessageId, showImages) : Promise.resolve();
    },
    [threads, showImages],
  );
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onRowHover = useCallback(
    (index: number) => {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = setTimeout(() => void warm(index), 120); // ignore rows the pointer only passes over
    },
    [warm],
  );

  function open(id: string) {
    const i = threads.findIndex((t) => t.id === id);
    // The opened conversation first (thread and newest body in parallel); neighbours only once it has landed,
    // so they never compete with what the reader is waiting for.
    const own = warm(i);
    setOpenId(id);
    void own.then(() => {
      void warm(i + 1);
      void warm(i - 1);
    });
  }

  /** In split views the reading pane follows the keyboard cursor once a conversation is open, like Outlook. */
  function moveCursor(next: number) {
    const clamped = Math.max(0, Math.min(threads.length - 1, next));
    setCursor(clamped);
    setKeyboardNav(true);
    void warm(clamped).then(() => warm(clamped + 1));
    if (!listOnly && openId !== null) {
      const t = threads[clamped];
      if (t) setOpenId(t.id);
    }
  }

  function toggleCheck(id: string, range: boolean) {
    const index = threads.findIndex((t) => t.id === id);
    setChecked((prev) => {
      const next = new Set(prev);
      if (range) {
        const [a, b] = [Math.min(anchor.current, index), Math.max(anchor.current, index)];
        for (let i = a; i <= b; i++) {
          const t = threads[i];
          if (t) next.add(t.id);
        }
      } else if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    anchor.current = index;
  }

  function toggleStar(id: string) {
    const t = threads.find((x) => x.id === id);
    if (t) action.mutate({ messageIds: [t.latestMessageId], action: t.starred ? "unstar" : "star", threadKey: t.id });
  }

  const current = threads[cursor];
  useShortcuts({
    next: () => moveCursor(cursor + 1),
    previous: () => moveCursor(cursor - 1),
    open: () => {
      setKeyboardNav(true);
      if (current) open(current.id);
    },
    back: () => setOpenId(null),
    select: () => current && toggleCheck(current.id, false),
    star: () => current && toggleStar(current.id),
    markRead: () => current && action.mutate({ messageIds: [current.latestMessageId], action: "read", threadKey: current.id }),
    markUnread: () => current && action.mutate({ messageIds: [current.latestMessageId], action: "unread", threadKey: current.id }),
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = pages;
  const onEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const openThread = threads.find((t) => t.id === openId) ?? null;

  let list: React.ReactNode;
  if (mailboxes.isSuccess && mailboxes.data.items.length > 0 && !box) {
    list = (
      <EmptyState icon={<FolderX />} title="Folder not found" action={<button type="button" className={s.link} onClick={() => navigate({ to: "/mail/$folder", params: { folder: "inbox" } })}>Go to Inbox</button>}>
        It may have been renamed or removed in another mail app.
      </EmptyState>
    );
  } else if (!box || pages.isPending) {
    list = <ListSkeleton rows={14} />;
  } else if (pages.isError) {
    list = (
      <EmptyState icon={<FolderX />} title="Could not load this folder" action={<button type="button" className={s.link} onClick={() => void pages.refetch()}>Try again</button>}>
        {pages.error.message}
      </EmptyState>
    );
  } else if (threads.length === 0) {
    list = (
      <EmptyState icon={<Inbox />} title={box.role === "inbox" ? "Inbox zero" : "Nothing here"}>
        {box.role === "inbox" ? "New mail will appear here as it arrives." : `${folderLabel(box)} is empty.`}
      </EmptyState>
    );
  } else {
    list = (
      <MessageList
        threads={threads}
        total={total}
        density={settings.density}
        twoLine={twoLine}
        cursor={keyboardNav ? cursor : -1}
        openId={openId}
        checked={checked}
        now={new Date()}
        onCursor={(i) => {
          setCursor(i);
          setKeyboardNav(false);
        }}
        onOpen={open}
        onToggleCheck={toggleCheck}
        onToggleStar={toggleStar}
        onEndReached={onEndReached}
        onRowHover={onRowHover}
        onRowPress={(i) => void warm(i)}
      />
    );
  }

  const listColumn = (
    <div className={s.listColumn} data-list-pane>
      <MailToolbar title={box ? folderLabel(box) : ""} shown={threads.length} total={total} checkedCount={checked.size} onClearSelection={() => setChecked(new Set())} onRefresh={() => void pages.refetch()} refreshing={pages.isRefetching} />
      <div className={s.listBody}>{list}</div>
    </div>
  );

  const reading = <ThreadView key={openThread?.id ?? "none"} threadKey={openThread?.id ?? null} fallbackSubject={openThread?.subject ?? ""} onBack={() => setOpenId(null)} showBack={listOnly} />;

  return (
    <div className={s.mail}>
      {listOnly ? (
        showingThread ? reading : listColumn
      ) : (
        <SplitPane
          direction={split === "right" ? "row" : "column"}
          label="Resize reading pane"
          size={settings.paneSizes[`mail-${split}`] ?? (split === "right" ? 42 : 50)}
          onSizeChange={(v) => updateSettings({ paneSizes: { ...settings.paneSizes, [`mail-${split}`]: v } })}
          first={listColumn}
          second={reading}
        />
      )}
    </div>
  );
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className={s.skeleton} aria-busy="true" aria-label="Loading conversations">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={s.skeletonRow}>
          <Skeleton width={32} height={32} radius={16} />
          <div className={s.skeletonText}>
            <Skeleton width={`${30 + ((i * 13) % 25)}%`} height={12} />
            <Skeleton width={`${55 + ((i * 7) % 35)}%`} height={10} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Approximate list pane width from the window and the saved split, enough to pick one- or two-line rows. */
function useListWidth(percent: number): number {
  const w = useMediaWidth();
  return ((w - 256) * percent) / 100;
}

function useMediaWidth(): number {
  const [w, setW] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}
